import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  useQuery,
  useMutation,
  useLazyQuery,
  useApolloClient,
} from '@apollo/client'
import { Box, Typography, Checkbox, Tooltip } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { DownloadCsvButton } from '../../components/Table/DownloadCsvButton'
import { DateFilterPill } from '../../components/Table/DateFilterPill'
import FullScreenLoader from '../../components/Shared/FullScreenLoader'
import { useSnackbar } from '../../components/Shared/useSnackbar'
import RTTable from '../../components/RTTable'
import { TableSearch } from '../../components/Table/TableSearch'
import { FilterPill } from '../../components/Table/FilterPill'
import { BulkActionsBar } from '../../components/Table/BulkActionsBar'
import { ParkingSubscriptionStatus } from '../../graphql/tenant/getTenant'
import {
  GET_TENANT_PARKING_POOL_ID,
  IGetTenantParkingPoolIdData,
  IGetTenantParkingPoolIdVars,
} from '../../graphql/parkingPool/getTenantParkingPoolId'
import {
  COUNT_PARKING_POOL_RESERVATIONS,
  IListParkingPoolReservationsData,
  IListParkingPoolReservationsVars,
  IParkingPoolReservationRow,
  LIST_PARKING_POOL_RESERVATIONS,
  ParkingPoolReservationsCancellation,
  ParkingPoolReservationsOrder,
} from '../../graphql/parkingPool/listParkingPoolReservations'
import {
  QUERY_USER,
  IQueryUserData,
  IQueryUserVars,
} from '../../graphql/users/queryUser'
import CancelReservationModal from '../../components/Parking/CancelReservationModal'
import { daysOfSubscription } from '../../utils/reservationDays'
import {
  BULK_CANCEL_PARKING_SUBSCRIPTIONS_FOR_USER,
  IBulkCancelParkingSubscriptionsForUserData,
  IBulkCancelParkingSubscriptionsForUserVars,
} from '../../graphql/parkingPool/bulkCancelParkingSubscriptionsForUser'
import { colors } from '../../const/colors'
import { useAppSelector } from '../../redux/store'
import { downloadCsv as exportCsv } from '../../utils/downloadCsv'
import { dateIso2localeString } from '../../utils/formatDate'
import {
  cancellationEvents,
  cancelledOnReservationDay,
  ICancellationSource,
} from '../../utils/cancellationEvents'

const DEFAULT_WINDOW_DAYS = 90
const ALL_HISTORY_SINCE = '1970-01-01T00:00:00Z'
const DEFAULT_PAGE_SIZE = 50
const PAGE_SIZE_OPTIONS = [20, 50, 100]
const SEARCH_DEBOUNCE_MS = 300

const windowStart = () =>
  dayjs().subtract(DEFAULT_WINDOW_DAYS, 'day').format('YYYY-MM-DD')

const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const formattedDates = new Map<string, string>()

const formatIsoDate = (isoStr: string): string => {
  if (!isoStr) return '—'
  const cached = formattedDates.get(isoStr)
  if (cached) return cached
  const formatted = dateFormatter.format(new Date(isoStr))
  formattedDates.set(isoStr, formatted)
  return formatted
}

type RowKind = 'subscription' | 'day'

interface IReservationRow {
  _id: string
  kind: RowKind
  subscriptionId: string
  login: string
  parking: string
  fullName: string
  email: string
  cardNumber: string
  periodLabel: string
  parentPeriodLabel: string
  validFromDisplay: string
  validToDisplay: string
  validFromRaw: string
  validToRaw: string
  subscriptionFromDisplay: string
  subscriptionToDisplay: string
  cancelledDayDisplay: string
  statusKey: string
  statusLabel: string
  status: ParkingSubscriptionStatus
  isActive: boolean
  isCancellable: boolean
  cancelledAtRaw: string
  cancelledBy: string
  cancelledTooLate: boolean
  cancelledDaysCount: number
  totalDaysCount: number
}

interface ISort {
  id: string
  desc: boolean
}

const CANCELLED_DAY = 'cancelled_day'

const ANY_CANCELLED = 'any_cancelled'

const CANCELLED_KEYS: string[] = [
  ParkingSubscriptionStatus.cancelled,
  CANCELLED_DAY,
]

const CANCELLED_OR_DONE: ParkingSubscriptionStatus[] = [
  ParkingSubscriptionStatus.cancelled,
  ParkingSubscriptionStatus.past,
]

const SUBSCRIPTION_STATUSES = Object.values(ParkingSubscriptionStatus) as string[]

const STATUS_ORDER: string[] = [
  ParkingSubscriptionStatus.active,
  ParkingSubscriptionStatus.new,
  ParkingSubscriptionStatus.pending,
  ParkingSubscriptionStatus.cancelled,
  CANCELLED_DAY,
  ANY_CANCELLED,
  ParkingSubscriptionStatus.past,
  ParkingSubscriptionStatus.error,
]

const STATUS_TONE: Record<string, { bg: string; color: string }> = {
  [ParkingSubscriptionStatus.active]: {
    bg: colors.greenBg,
    color: colors.green,
  },
  [ParkingSubscriptionStatus.new]: { bg: '#FBF1CC', color: '#8a6a00' },
  [ParkingSubscriptionStatus.pending]: { bg: '#FBF1CC', color: '#8a6a00' },
  [ParkingSubscriptionStatus.past]: {
    bg: colors.border2,
    color: colors.textMuted,
  },
  [ParkingSubscriptionStatus.cancelled]: {
    bg: colors.redBg,
    color: colors.red,
  },
  [ParkingSubscriptionStatus.error]: { bg: colors.redBg, color: colors.red },
  [CANCELLED_DAY]: { bg: colors.redBg, color: colors.red },
}

const DEFAULT_SORT: ISort = { id: 'validFromRaw', desc: true }

const ORDER_BY_COLUMN: Record<
  string,
  [ParkingPoolReservationsOrder, ParkingPoolReservationsOrder]
> = {
  validFromRaw: [
    ParkingPoolReservationsOrder.valid_from_asc,
    ParkingPoolReservationsOrder.valid_from_desc,
  ],
  fullName: [
    ParkingPoolReservationsOrder.name_asc,
    ParkingPoolReservationsOrder.name_desc,
  ],
  parking: [
    ParkingPoolReservationsOrder.parking_asc,
    ParkingPoolReservationsOrder.parking_desc,
  ],
  statusLabel: [
    ParkingPoolReservationsOrder.status_asc,
    ParkingPoolReservationsOrder.status_desc,
  ],
  cancelledAtRaw: [
    ParkingPoolReservationsOrder.cancelled_at_asc,
    ParkingPoolReservationsOrder.cancelled_at_desc,
  ],
}

const toCancellationSource = (
  row: IParkingPoolReservationRow,
): ICancellationSource => ({
  id: row.subscription_id,
  validFrom: row.valid_from,
  validTo: row.valid_to ?? '',
  status: row.status as ParkingSubscriptionStatus,
  cancelledAt: row.cancelled_at,
  cancelledBy: row.cancelled_by,
  parkingPass: { parking: row.parking ? { name: row.parking } : null },
  cancelledDays: (row.cancelled_days ?? []).map(slot => ({
    day: slot.day,
    cancelledAt: slot.cancelled_at,
    cancelledBy: slot.cancelled_by,
  })),
})

const Reservations = () => {
  const { t, i18n } = useTranslation(['parking_pool_reservations', 'generic'])
  const statusLabel = (status: string) =>
    t(`status_${status}`, { defaultValue: status })
  const { tenantName } = useAppSelector(state => state.user)
  const client = useApolloClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState(windowStart)
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<ISort>(DEFAULT_SORT)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [cancelTarget, setCancelTarget] = useState<IReservationRow | null>(null)
  const { notify, notifyError } = useSnackbar()

  const resetPaging = useCallback(() => {
    setPage(0)
    setSelectedIds([])
  }, [])

  useEffect(() => {
    const trimmed = search.trim()
    if (trimmed === debouncedSearch) return
    const timer = setTimeout(() => {
      setDebouncedSearch(trimmed)
      resetPaging()
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, debouncedSearch, resetPaging])

  const changeStatusFilter = (value: string) => {
    setStatusFilter(value)
    resetPaging()
  }

  const changeDateFrom = (value: string) => {
    setDateFrom(value)
    resetPaging()
  }

  const changeDateTo = (value: string) => {
    setDateTo(value)
    resetPaging()
  }

  const { data: poolData, loading: poolLoading } = useQuery<
    IGetTenantParkingPoolIdData,
    IGetTenantParkingPoolIdVars
  >(GET_TENANT_PARKING_POOL_ID, {
    variables: { tenant: tenantName },
    skip: !tenantName,
  })

  const poolId = poolData?.getTenant?.parkingPool?.id

  const effectiveSort =
    statusFilter === CANCELLED_DAY && sort.id === 'cancelledAtRaw'
      ? DEFAULT_SORT
      : sort

  const filterVars = useMemo(() => {
    const isSubscriptionStatus = SUBSCRIPTION_STATUSES.includes(statusFilter)
    return {
      parking_pool: poolId ?? '',
      from: dateFrom ? `${dateFrom}T00:00:00Z` : ALL_HISTORY_SINCE,
      to: dateTo ? `${dateTo}T00:00:00Z` : undefined,
      search: debouncedSearch || undefined,
      status: isSubscriptionStatus
        ? [statusFilter as ParkingSubscriptionStatus]
        : undefined,
      cancellation:
        statusFilter === CANCELLED_DAY
          ? ParkingPoolReservationsCancellation.day
          : statusFilter === ANY_CANCELLED
          ? ParkingPoolReservationsCancellation.any
          : undefined,
    }
  }, [poolId, dateFrom, dateTo, debouncedSearch, statusFilter])

  const order =
    ORDER_BY_COLUMN[effectiveSort.id]?.[effectiveSort.desc ? 1 : 0] ??
    ParkingPoolReservationsOrder.valid_from_desc

  const { data, previousData, loading, refetch } = useQuery<
    IListParkingPoolReservationsData,
    IListParkingPoolReservationsVars
  >(LIST_PARKING_POOL_RESERVATIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !poolId,
    variables: {
      ...filterVars,
      order,
      first: pageSize,
      offset: page * pageSize,
    },
  })

  const payload = (data ?? previousData)?.listParkingPoolReservations

  useEffect(() => {
    const result = data?.listParkingPoolReservations
    if (result && !result.status)
      notifyError(result.error || result.message || String(result.code))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const hasDayBucket = !!payload?.status_counts?.some(
    item => item.status === CANCELLED_DAY,
  )

  const { data: dayCountData, refetch: refetchDayCount } = useQuery<
    IListParkingPoolReservationsData,
    IListParkingPoolReservationsVars
  >(COUNT_PARKING_POOL_RESERVATIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !poolId || !payload || hasDayBucket,
    variables: {
      parking_pool: filterVars.parking_pool,
      from: filterVars.from,
      to: filterVars.to,
      search: filterVars.search,
      cancellation: ParkingPoolReservationsCancellation.day,
    },
  })

  const totalCount = payload?.total_count ?? 0
  const lastPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)

  useEffect(() => {
    if (payload && page > lastPage) setPage(lastPage)
  }, [payload, page, lastPage])

  const refreshReservations = useCallback(() => {
    refetch()
    if (!hasDayBucket) refetchDayCount()
  }, [refetch, refetchDayCount, hasDayBucket])

  const [loadUserDays, { data: userDaysData, loading: userDaysLoading }] =
    useLazyQuery<IQueryUserData, IQueryUserVars>(QUERY_USER, {
      fetchPolicy: 'no-cache',
    })

  const [bulkCancelReservations] = useMutation<
    IBulkCancelParkingSubscriptionsForUserData,
    IBulkCancelParkingSubscriptionsForUserVars
  >(BULK_CANCEL_PARKING_SUBSCRIPTIONS_FOR_USER, { onError: notifyError })

  const buildRows = useCallback(
    (source: IParkingPoolReservationRow[]): IReservationRow[] => {
      const period = (from: string, to: string) =>
        `${formatIsoDate(from)} – ${formatIsoDate(to)}`

      return source.flatMap(item => {
        const sub = toCancellationSource(item)
        const events = cancellationEvents([sub])
        const whole = events.find(event => event.kind === 'subscription')
        const dayEvents = events.filter(event => event.kind === 'day')
        const subPeriod = period(sub.validFrom, sub.validTo)
        const base = {
          subscriptionId: String(item.subscription_id),
          login: item.login,
          fullName: [item.first_name, item.last_name].filter(Boolean).join(' '),
          email: item.email || '—',
          cardNumber: item.card_number || '—',
        }

        const reservationRow: IReservationRow = {
          ...base,
          _id: base.subscriptionId,
          kind: 'subscription',
          parking: item.parking || '—',
          periodLabel: subPeriod,
          parentPeriodLabel: '',
          validFromRaw: sub.validFrom,
          validToRaw: sub.validTo,
          validFromDisplay: formatIsoDate(sub.validFrom),
          validToDisplay: formatIsoDate(sub.validTo),
          subscriptionFromDisplay: formatIsoDate(sub.validFrom),
          subscriptionToDisplay: formatIsoDate(sub.validTo),
          cancelledDayDisplay: '',
          statusKey: sub.status,
          statusLabel: statusLabel(sub.status),
          status: sub.status,
          isActive: sub.status === ParkingSubscriptionStatus.active,
          isCancellable: !CANCELLED_OR_DONE.includes(sub.status),
          cancelledAtRaw: whole?.cancelledAt ?? '',
          cancelledBy: whole?.cancelledBy ?? '',
          cancelledTooLate: !!whole && cancelledOnReservationDay(whole),
          cancelledDaysCount: dayEvents.length,
          totalDaysCount: item.total_days_count,
        }

        const dayRows = dayEvents.map<IReservationRow>(event => ({
          ...base,
          _id: `${base.subscriptionId}-${event.day}`,
          kind: 'day',
          parking: event.parking ?? reservationRow.parking,
          periodLabel: formatIsoDate(event.day as string),
          parentPeriodLabel: subPeriod,
          validFromRaw: event.day as string,
          validToRaw: event.day as string,
          validFromDisplay: formatIsoDate(event.day as string),
          validToDisplay: formatIsoDate(event.day as string),
          subscriptionFromDisplay: formatIsoDate(sub.validFrom),
          subscriptionToDisplay: formatIsoDate(sub.validTo),
          cancelledDayDisplay: formatIsoDate(event.day as string),
          statusKey: CANCELLED_DAY,
          statusLabel: t(`status_${CANCELLED_DAY}`),
          status: sub.status,
          isActive: false,
          isCancellable: false,
          cancelledAtRaw: event.cancelledAt ?? '',
          cancelledBy: event.cancelledBy ?? '',
          cancelledTooLate: cancelledOnReservationDay(event),
          cancelledDaysCount: 0,
          totalDaysCount: 0,
        }))

        return [reservationRow, ...dayRows]
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  )

  const visibleRows = useCallback(
    (rows: IReservationRow[]) => {
      let visible = rows

      if (statusFilter === ANY_CANCELLED) {
        visible = visible.filter(row => CANCELLED_KEYS.includes(row.statusKey))
      } else if (statusFilter) {
        visible = visible.filter(row => row.statusKey === statusFilter)
      }

      if (dateFrom) {
        visible = visible.filter(
          row => row.kind !== 'day' || row.validToRaw.slice(0, 10) >= dateFrom,
        )
      }

      if (dateTo) {
        visible = visible.filter(
          row => row.kind !== 'day' || row.validFromRaw.slice(0, 10) <= dateTo,
        )
      }

      return visible
    },
    [statusFilter, dateFrom, dateTo],
  )

  const filteredRows = useMemo(
    () => visibleRows(buildRows(payload?.rows ?? [])),
    [payload, buildRows, visibleRows],
  )

  const statusOptions = useMemo(() => {
    const counts = new Map<string, number>(
      (payload?.status_counts ?? []).map(item => [item.status, item.count]),
    )
    const dayCount = dayCountData?.listParkingPoolReservations.total_count
    if (!counts.has(CANCELLED_DAY) && dayCount !== undefined)
      counts.set(CANCELLED_DAY, dayCount)

    const options = [{ value: '', label: t('filter_status_all') }]

    const cancelledKinds = CANCELLED_KEYS.filter(key => counts.get(key))
    const cancelledCount =
      cancelledKinds.length > 1
        ? counts.get(ANY_CANCELLED) ??
          cancelledKinds.reduce((sum, key) => sum + (counts.get(key) ?? 0), 0)
        : 0

    STATUS_ORDER.forEach(status => {
      const count =
        status === ANY_CANCELLED ? cancelledCount : counts.get(status)
      if (count)
        options.push({
          value: status,
          label: `${statusLabel(status)} (${count})`,
        })
    })

    return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, dayCountData, t])

  const selectableRowIds = useMemo(
    () => filteredRows.map(row => row._id),
    [filteredRows],
  )

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const selectedRows = useMemo(
    () => filteredRows.filter(row => selectedSet.has(row._id)),
    [filteredRows, selectedSet],
  )

  const cancellableSubscriptionIds = useMemo(
    () =>
      Array.from(
        new Set(
          selectedRows
            .filter(row => row.isCancellable)
            .map(row => row.subscriptionId),
        ),
      ),
    [selectedRows],
  )

  const allSelected =
    selectableRowIds.length > 0 &&
    selectableRowIds.every(id => selectedSet.has(id))
  const someSelected = selectableRowIds.some(id => selectedSet.has(id))

  const toggleAll = useCallback(() => {
    setSelectedIds(allSelected ? [] : selectableRowIds)
  }, [allSelected, selectableRowIds])

  const toggleRow = useCallback((rowId: string) => {
    setSelectedIds(prev =>
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId],
    )
  }, [])

  const clearSelection = useCallback(() => setSelectedIds([]), [])

  const openCancel = useCallback(
    (row: IReservationRow) => {
      setCancelTarget(row)
      loadUserDays({ variables: { login: row.login } })
    },
    [loadUserDays],
  )

  const closeCancel = useCallback(() => setCancelTarget(null), [])

  const cancelTargetDays = useMemo(() => {
    if (!cancelTarget) return []
    const user = userDaysData?.queryUser[0]
    if (!user || user.login !== cancelTarget.login) return []
    const sub = user.parkingSubscriptions?.find(
      item => String(item.id) === cancelTarget.subscriptionId,
    )
    return sub ? daysOfSubscription(sub) : []
  }, [cancelTarget, userDaysData])

  const bulkCancel = useCallback(async () => {
    const ids = cancellableSubscriptionIds
    if (!ids.length) return
    clearSelection()
    const res = await bulkCancelReservations({
      variables: {
        parkingSubscriptions: ids,
        comment: t('cancel_default_comment'),
        notify: true,
      },
    })
    const payload = res.data?.bulkCancelParkingSubscriptionsForUser
    if (!payload) return
    if (payload.failed > 0) {
      notify(
        t('toast_cancelled_partial', {
          ok: payload.succeeded,
          failed: payload.failed,
        }),
      )
    } else {
      notify(t('toast_cancelled_many', { n: payload.succeeded }))
    }
    refreshReservations()
  }, [
    cancellableSubscriptionIds,
    clearSelection,
    bulkCancelReservations,
    notify,
    refreshReservations,
    t,
  ])

  const fetchAllRows = useCallback(async () => {
    const res = await client.query<
      IListParkingPoolReservationsData,
      IListParkingPoolReservationsVars
    >({
      query: LIST_PARKING_POOL_RESERVATIONS,
      fetchPolicy: 'no-cache',
      variables: { ...filterVars, order, first: 0 },
    })
    const result = res.data.listParkingPoolReservations
    if (!result.status)
      throw new Error(result.error || result.message || String(result.code))
    return visibleRows(buildRows(result.rows ?? []))
  }, [client, filterVars, order, visibleRows, buildRows])

  const downloadCsv = useCallback(async () => {
    const cancelledDaysCell = (row: IReservationRow) => {
      if (row.kind === 'day' || !row.cancelledDaysCount) return ''
      return row.totalDaysCount
        ? `${row.cancelledDaysCount}/${row.totalDaysCount}`
        : String(row.cancelledDaysCount)
    }

    if (!selectedRows.length && (!poolId || !payload?.total_count)) return
    notify(t('toast_generating_csv'))

    let rowsToExport = selectedRows
    if (!rowsToExport.length) {
      try {
        rowsToExport = await fetchAllRows()
      } catch (error) {
        notifyError(error as Error)
        return
      }
    }
    if (!rowsToExport.length) return

    const csvRows = [
      [
        t('col_row_type'),
        t('col_parking'),
        t('col_full_name'),
        t('col_email'),
        t('col_card_number'),
        t('col_valid_from'),
        t('col_valid_to'),
        t('col_cancelled_day'),
        t('col_status'),
        t('col_cancelled_at'),
        t('col_cancelled_by'),
        t('col_cancelled_same_day'),
        t('col_cancelled_days'),
      ],
      ...rowsToExport.map(row => [
        row.kind === 'day'
          ? t(`status_${CANCELLED_DAY}`)
          : t('row_type_reservation'),
        row.parking,
        row.fullName,
        row.email,
        row.cardNumber,
        row.subscriptionFromDisplay,
        row.subscriptionToDisplay,
        row.cancelledDayDisplay,
        row.statusLabel,
        row.cancelledAtRaw
          ? dateIso2localeString(row.cancelledAtRaw, i18n.language)
          : '',
        row.cancelledBy,
        row.cancelledTooLate ? t('generic:yes') : '',
        cancelledDaysCell(row),
      ]),
    ]
    const cancelledOnly = rowsToExport.every(row =>
      CANCELLED_KEYS.includes(row.statusKey),
    )

    exportCsv(
      csvRows,
      cancelledOnly
        ? 'anulowane-rezerwacje-parkingowe.csv'
        : 'rezerwacje-parkingowe.csv',
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, poolId, payload, fetchAllRows, t, i18n.language])

  const tableColumns = useMemo(
    () =>
      [
        {
          id: 'selection',
          disableSortBy: true,
          Header: () => (
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={toggleAll}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
              size="small"
              sx={{
                color: colors.borderCheck,
                '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                  color: colors.navy,
                },
              }}
            />
          ),
          Cell: ({ row }: { row: any }) => (
            <Checkbox
              checked={selectedSet.has(row.original._id)}
              onChange={() => toggleRow(row.original._id)}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
              size="small"
              inputProps={{
                'aria-label': `${row.original.fullName}, ${row.original.periodLabel}`,
              }}
              sx={{
                color: colors.borderCheck,
                '&.Mui-checked': { color: colors.navy },
              }}
            />
          ),
        },
        {
          Header: t('col_parking'),
          accessor: 'parking',
          Cell: ({ value }: { value: string }) => (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {value}
            </Box>
          ),
        },
        {
          Header: t('col_full_name'),
          accessor: 'fullName',
          Cell: ({ value }: { value: string }) => (
            <Box component="span" sx={{ fontWeight: 700 }}>
              {value}
            </Box>
          ),
        },
        {
          Header: t('col_email'),
          accessor: 'email',
          disableSortBy: true,
          Cell: ({ value }: { value: string }) => (
            <Box
              component="span"
              sx={{ color: colors.textMuted, fontSize: '13.5px' }}
            >
              {value}
            </Box>
          ),
        },
        {
          Header: t('col_card_number'),
          accessor: 'cardNumber',
          disableSortBy: true,
          Cell: ({ value }: { value: string }) => (
            <Box
              component="span"
              sx={{
                color: colors.textMuted,
                fontSize: '13.5px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value}
            </Box>
          ),
        },
        {
          Header: t('col_period'),
          accessor: 'validFromRaw',
          Cell: ({ row }: { row: any }) => (
            <Box>
              <Box
                sx={{
                  color: colors.textSecondary,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.original.periodLabel}
              </Box>
              {row.original.kind === 'day' && (
                <Box
                  sx={{
                    fontSize: '11.5px',
                    color: colors.slateLight,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('day_of_reservation', {
                    period: row.original.parentPeriodLabel,
                  })}
                </Box>
              )}
            </Box>
          ),
        },
        {
          Header: t('col_status'),
          accessor: 'statusLabel',
          Cell: ({ row, value }: { row: any; value: string }) => {
            const { statusKey, cancelledDaysCount, totalDaysCount } =
              row.original
            const tone =
              STATUS_TONE[statusKey] ??
              STATUS_TONE[ParkingSubscriptionStatus.error]
            return (
              <Box>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    background: tone.bg,
                    color: tone.color,
                    fontWeight: 700,
                    fontSize: '12.5px',
                    px: '11px',
                    py: '4px',
                    borderRadius: colors.radiusTag,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {value}
                </Box>
                {cancelledDaysCount > 0 &&
                  statusKey !== ParkingSubscriptionStatus.pending && (
                    <Box
                      sx={{
                        mt: '3px',
                        fontSize: '11.5px',
                        fontWeight: 700,
                        color: colors.red,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {totalDaysCount
                        ? t('days_cancelled_of', {
                            count: cancelledDaysCount,
                            total: totalDaysCount,
                          })
                        : t('days_cancelled', { count: cancelledDaysCount })}
                    </Box>
                  )}
              </Box>
            )
          },
        },
        {
          Header: t('col_cancelled_at'),
          accessor: 'cancelledAtRaw',
          disableSortBy: statusFilter === CANCELLED_DAY,
          Cell: ({ row }: { row: any }) => {
            const { kind, statusKey, cancelledAtRaw, cancelledBy } =
              row.original
            const isCancelled =
              kind === 'day' ||
              statusKey === ParkingSubscriptionStatus.cancelled
            if (!isCancelled)
              return (
                <Box component="span" sx={{ color: colors.slateLight }}>
                  —
                </Box>
              )
            return (
              <Box>
                <Box
                  sx={{
                    fontWeight: 700,
                    fontSize: '13px',
                    color: colors.navy,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cancelledAtRaw
                    ? dateIso2localeString(cancelledAtRaw, i18n.language)
                    : t('cancelled_time_unknown')}
                </Box>
                {!!cancelledBy && (
                  <Box
                    sx={{
                      fontSize: '11.5px',
                      color: colors.textMuted,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cancelledBy}
                  </Box>
                )}
                {row.original.cancelledTooLate && (
                  <Box
                    sx={{
                      fontSize: '11.5px',
                      fontWeight: 700,
                      color: colors.red,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('cancelled_same_day')}
                  </Box>
                )}
              </Box>
            )
          },
        },
        {
          id: 'action',
          disableSortBy: true,
          Header: t('col_actions'),
          Cell: ({ row }: { row: any }) => {
            if (
              row.original.kind === 'day' ||
              row.original.status === ParkingSubscriptionStatus.cancelled
            )
              return null
            if (row.original.isCancellable) {
              return (
                <Box
                  data-e2e="cancel-reservation"
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation()
                    openCancel(row.original)
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: '34px',
                    px: '14px',
                    borderRadius: '10px',
                    border: `1.5px solid ${colors.redBorder}`,
                    color: colors.red,
                    fontWeight: 700,
                    fontSize: '12.5px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background .12s',
                    '&:hover': { background: colors.redBg },
                  }}
                >
                  {t('cancel')}
                </Box>
              )
            }
            return (
              <Tooltip
                title={t('no_action_for_status', {
                  status: row.original.statusLabel,
                })}
                placement="top"
                arrow
              >
                <Box
                  component="span"
                  sx={{
                    color: colors.slateLight,
                    fontWeight: 700,
                    fontSize: '12.5px',
                    cursor: 'default',
                  }}
                >
                  —
                </Box>
              </Tooltip>
            )
          },
        },
      ] as any,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allSelected,
      someSelected,
      toggleAll,
      selectedSet,
      toggleRow,
      openCancel,
      statusFilter,
      t,
      i18n.language,
    ],
  )

  if (poolLoading || (loading && !payload)) return <FullScreenLoader />

  return (
    <>
      <Box
        sx={{
          fontSize: '12px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.green,
          fontWeight: 800,
          mb: '6px',
        }}
      >
        {t('eyebrow')}
      </Box>
      <Typography
        component="h1"
        sx={{
          fontSize: '30px',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: colors.navy,
          lineHeight: 1.15,
          mb: '4px',
        }}
      >
        {t('title')}
      </Typography>
      <Typography
        sx={{ fontSize: '14.5px', color: colors.textMuted, mb: '24px' }}
      >
        {t('subtitle')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          mb: '18px',
          flexWrap: 'wrap',
        }}
      >
        <TableSearch
          value={search}
          onChange={setSearch}
          placeholder={t('search_placeholder')}
        />

        <FilterPill
          label={t('filter_status')}
          value={statusFilter}
          options={statusOptions}
          onSelect={changeStatusFilter}
        />

        <DateFilterPill
          e2e="filter-date-from"
          label={t('generic:from')}
          value={dateFrom}
          onChange={changeDateFrom}
        />
        <DateFilterPill
          e2e="filter-date-to"
          label={t('generic:to')}
          value={dateTo}
          onChange={changeDateTo}
        />

        <DownloadCsvButton
          onClick={downloadCsv}
          label={
            selectedRows.length
              ? t('download_selected_csv', { count: selectedRows.length })
              : undefined
          }
        />
      </Box>
      <BulkActionsBar
        count={selectedRows.length}
        onClear={clearSelection}
        color="danger"
        actions={[
          {
            label: t('download_selected_csv', { count: selectedRows.length }),
            onClick: downloadCsv,
            icon: <DownloadIcon sx={{ fontSize: 16 }} />,
          },
          {
            label: !cancellableSubscriptionIds.length
              ? t('bulk_cancel_none')
              : cancellableSubscriptionIds.length < selectedRows.length
              ? t('bulk_cancel_some', {
                  count: cancellableSubscriptionIds.length,
                })
              : t('bulk_cancel'),
            e2e: 'bulk-cancel',
            onClick: bulkCancel,
            variant: 'danger',
            disabled: !cancellableSubscriptionIds.length,
            icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
          },
        ]}
      />

      <RTTable
        columns={tableColumns}
        data={filteredRows}
        pagination
        gFilter={false}
        serverSide={{
          total: totalCount,
          page: Math.min(page, lastPage),
          pageSize,
          rowsPerPageOptions: PAGE_SIZE_OPTIONS,
          onPageChange: nextPage => {
            setPage(nextPage)
            setSelectedIds([])
          },
          onPageSizeChange: nextSize => {
            setPageSize(nextSize)
            resetPaging()
          },
          sortBy: effectiveSort,
          onSortChange: (id, desc) => {
            setSort({ id, desc })
            resetPaging()
          },
          labelDisplayedRows: ({ from, to, count }) =>
            t('pagination_reservations', { from, to, count }),
        }}
      />

      {cancelTarget && (
        <CancelReservationModal
          open
          onClose={closeCancel}
          login={cancelTarget.login}
          subscriptionId={cancelTarget.subscriptionId}
          fullName={cancelTarget.fullName}
          parking={cancelTarget.parking}
          periodLabel={`${cancelTarget.validFromDisplay} – ${cancelTarget.validToDisplay}`}
          days={cancelTargetDays}
          daysLoading={userDaysLoading}
          onCancelled={refreshReservations}
        />
      )}
    </>
  )
}

export default Reservations
